import React from 'react'
import { Card, CardContent, CardHeader, Label, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { listPlans } from '@/modules/plans/services/planService'
import type { Plan } from '@/modules/plans/types'
import {
  fetchPlanFiboTargetsPolicies,
  fetchRoleFiboTargetsPolicies,
  upsertPlanFiboTargetsPolicy,
  upsertRoleFiboTargetsPolicy,
} from '../services/fiboTargetsSettingsService'

export default function FiboTargetsSettings() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [rolePolicies, setRolePolicies] = React.useState<Record<string, boolean>>({})
  const [planPolicies, setPlanPolicies] = React.useState<Record<string, boolean>>({})
  const [plans, setPlans] = React.useState<Plan[]>([])

  React.useEffect(() => {
    let mounted = true

    async function load() {
      try {
        const [roles, plansPolicies, plansResult] = await Promise.all([
          fetchRoleFiboTargetsPolicies(),
          fetchPlanFiboTargetsPolicies(),
          listPlans({ page: 1, pageSize: 100 }),
        ])
        if (!mounted) return

        const rolesMap: Record<string, boolean> = {}
        roles.forEach((r) => {
          rolesMap[r.role] = !!r.fibo_targets_enabled
        })

        const plansMap: Record<string, boolean> = {}
        plansPolicies.forEach((p) => {
          plansMap[p.plan_id] = !!p.fibo_targets_enabled
        })

        setRolePolicies(rolesMap)
        setPlanPolicies(plansMap)
        setPlans(plansResult.items)
      } catch (e: any) {
        toast({
          title: 'Alvos (Projeção Fibo)',
          description: e?.message || 'Falha ao carregar configurações',
          variant: 'error',
        })
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [toast])

  async function toggleRole(role: string, next: boolean) {
    if (saving) return
    if (role === 'master') return
    setSaving(true)
    try {
      await upsertRoleFiboTargetsPolicy(role, next)
      setRolePolicies((prev) => ({ ...prev, [role]: next }))
      toast({
        title: 'Alvos (Projeção Fibo) - Roles',
        description: `Overlay para role "${role}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Alvos (Projeção Fibo) - Roles',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  async function togglePlan(planId: string, next: boolean) {
    if (saving) return
    setSaving(true)
    try {
      await upsertPlanFiboTargetsPolicy(planId, next)
      setPlanPolicies((prev) => ({ ...prev, [planId]: next }))
      toast({
        title: 'Alvos (Projeção Fibo) - Planos',
        description: `Overlay para plano "${planId}" ${next ? 'ativado' : 'desativado'}.`,
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Alvos (Projeção Fibo) - Planos',
        description: e?.message || 'Falha ao salvar',
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Alvos (Projeção Fibo)</CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por papel (role)</div>
          <p className="text-xs text-foreground/60">
            Controla quem pode visualizar o checkbox e o overlay de alvos por projeção de Fibonacci (1 / 1.618 / 2.618) no gráfico.
            O master sempre tem acesso.
          </p>
          <div className="mt-2 space-y-2">
            {['master', 'member', 'afiliado'].map((role) => (
              <div
                key={role}
                className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
              >
                <div className="text-sm font-medium capitalize">{role}</div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch
                    checked={role === 'master' ? true : !!rolePolicies[role]}
                    disabled={loading || saving || role === 'master'}
                    onChange={(e) => toggleRole(role, (e.target as HTMLInputElement).checked)}
                  />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium text-foreground/80">Acesso por plano</div>
          <p className="text-xs text-foreground/60">
            Para liberar para usuários não-master, ative o role e os planos desejados. O acesso efetivo considera role + plano.
          </p>
          <div className="mt-2 space-y-2">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className="flex items-center justify-between gap-4 rounded-md border border-border/60 px-3 py-2"
              >
                <div className="text-sm font-medium">
                  {plan.name} <span className="text-xs text-foreground/60">({plan.id})</span>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Desativado</Label>
                  <Switch
                    checked={!!planPolicies[plan.id]}
                    disabled={loading || saving}
                    onChange={(e) => togglePlan(plan.id, (e.target as HTMLInputElement).checked)}
                  />
                  <Label className="text-xs">Ativado</Label>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

